"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Clock, ShieldCheck, PhoneCall, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Loader as Loader2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
interface TrustBadgeItem {
  iconName: "clock" | "shield";
  title: string;
  description: string;
}
const trustBadges: TrustBadgeItem[] = [{
  iconName: "clock",
  title: "24/7 Rapid response",
  description: "Plumbers dispatched across Jonesboro for active pipe and slab leaks."
}, {
  iconName: "shield",
  title: "Licensed & fully insured",
  description: "Certified leak detection specialists with non-invasive acoustic gear."
}];
function LeakDetectionForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <Alert className="border-border bg-secondary/50 p-6">
        <CheckCircle2 className="size-5 text-accent" />
        <AlertTitle className="text-base font-semibold text-foreground">
          Inspection request received
        </AlertTitle>
        <AlertDescription className="mt-2 text-sm text-muted-foreground">
          {successMessage || "Your inspection request has been received. We'll be in touch soon."}
        </AlertDescription>
        <div className="mt-4">
          <Button type="button" variant="outline" size="sm" onClick={resetForm} className="rounded-full">
            Submit another request
          </Button>
        </div>
      </Alert>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      {submitError && <Alert variant="destructive" className="mb-4">
          <AlertCircle className="size-4" />
          <AlertTitle>Submission error</AlertTitle>
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField control={control} name="full_name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground text-sm font-medium">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="focus-visible:ring-ring focus-visible:border-ring rounded-md" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground text-sm font-medium">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 123-4567" className="focus-visible:ring-ring focus-visible:border-ring rounded-md" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="sm:col-span-2">
          <FormField control={control} name="property_address" rules={{
          required: "Property address is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground text-sm font-medium">Property Address</FormLabel>
                <FormControl>
                  <Input placeholder="123 Main St" className="focus-visible:ring-ring focus-visible:border-ring rounded-md" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div>
          <FormField control={control} name="city" rules={{
          required: "City is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground text-sm font-medium">City</FormLabel>
                <FormControl>
                  <Input placeholder="Jonesboro" className="focus-visible:ring-ring focus-visible:border-ring rounded-md" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      <FormField control={control} name="leak_type" rules={{
      required: "Please select a leak type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground text-sm font-medium">Leak Type</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="w-full focus-visible:ring-ring focus-visible:border-ring rounded-md">
                  <SelectValue placeholder="Select suspected leak source" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="Slab Leak">Slab Leak</SelectItem>
                <SelectItem value="Pipe Leak">Pipe Leak</SelectItem>
                <SelectItem value="Water Heater Leak">Water Heater Leak</SelectItem>
                <SelectItem value="Fixture Leak">Fixture Leak</SelectItem>
                <SelectItem value="Unknown / Not Sure">Unknown / Not Sure</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="additional_details" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground text-sm font-medium">Additional Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Provide any extra information about the leak..." className="focus-visible:ring-ring focus-visible:border-ring min-h-24 resize-y rounded-md" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="consent" rules={{
      required: "Consent is required to proceed"
    }} render={({
      field
    }) => <FormItem className="flex flex-row items-start gap-3 space-y-0 pt-2">
            <FormControl>
              <Checkbox checked={field.value} onCheckedChange={field.onChange} className="mt-0.5 focus-visible:ring-ring" />
            </FormControl>
            <div className="grid gap-1.5 leading-none">
              <FormLabel className="text-muted-foreground text-xs leading-normal font-normal cursor-pointer">
                I agree to be contacted about my leak detection request
              </FormLabel>
              <FormMessage />
            </div>
          </FormItem>} />

      {/* Hidden source page input */}
      <input type="hidden" name="source_page" value="Leak Detection Repair Page" />

      <Button type="submit" disabled={isSubmitting} className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-md w-full rounded-full py-6 font-semibold transition-colors">
        {isSubmitting ? <>
            <Loader2 className="mr-2 size-4 animate-spin" />
            Submitting Request...
          </> : "Request Leak Inspection"}
      </Button>
    </Form>;
}
export default function Leakdetectionservicerequest() {
  const shouldReduceMotion = useReducedMotion();
  const animationProps = shouldReduceMotion ? {
    initial: {
      opacity: 1,
      y: 0
    },
    whileInView: {
      opacity: 1,
      y: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 14
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      amount: 0.2
    },
    transition: {
      duration: 0.32,
      ease: [0.16, 1, 0.3, 1]
    }
  };
  return <section id="leakdetectionservicerequest" data-nav="light" className="bg-background text-foreground relative py-16 lg:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Left Trust & Contact Column */}
          <motion.div {...animationProps} className="lg:col-span-5 space-y-8">
            <div className="space-y-4">
              <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20 px-3 py-1 font-medium">
                Emergency Water Leak Response
              </Badge>
              <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl font-default">
                Request Leak Detection Service
              </h2>
              <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
                Hidden pipe or slab leaks can damage foundations fast. Tell us what you are seeing, and our local technicians will pinpoint the exact source without destructive teardowns.
              </p>
            </div>

            <div className="space-y-4 pt-2">
              {trustBadges.map((badge, i) => <div key={badge.title} data-index={i} className="bg-card border-border/80 flex items-start gap-4 rounded-xl border p-4 shadow-2xs">
                  <div className="bg-primary/10 text-primary mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-lg">
                    {badge.iconName === "clock" ? <Clock className="size-5" /> : <ShieldCheck className="size-5" />}
                  </div>
                  <div>
                    <h3 className="text-foreground text-base font-semibold">{badge.title}</h3>
                    <p className="text-muted-foreground text-sm leading-normal">{badge.description}</p>
                  </div>
                </div>)}
            </div>

            <div className="bg-primary text-primary-foreground rounded-2xl p-6 shadow-md">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-primary-foreground/80 text-xs font-medium uppercase tracking-wider">
                    Immediate Emergency Assistance
                  </p>
                  <p className="text-lg font-bold">Need a plumber on site now?</p>
                </div>
                <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90 shrink-0 rounded-full font-semibold shadow-sm">
                  <a href="tel:+14045550199" className="inline-flex items-center gap-2">
                    <PhoneCall className="size-4" />
                    Call (404) 555-0199
                  </a>
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Right Form Card Column */}
          <motion.div {...animationProps} className="lg:col-span-7">
            <Card className="border-border/80 bg-card shadow-lg sm:p-2 rounded-2xl">
              <CardHeader className="space-y-1 pb-4">
                <CardTitle className="text-xl font-bold tracking-tight text-foreground sm:text-2xl font-default">
                  Schedule Your Inspection
                </CardTitle>
                <CardDescription className="text-muted-foreground text-sm">
                  Complete the details below for prioritized emergency dispatch across Jonesboro and nearby areas.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-2">
                <FormProvider formId="leak_detection_service_form" sectionName="leak-detection-service-request" defaultValues={{
                full_name: "",
                phone: "",
                property_address: "",
                city: "Jonesboro",
                leak_type: "",
                additional_details: "",
                consent: false,
                source_page: "Leak Detection Repair Page"
              }} validationRules={{
                full_name: {
                  required: true
                },
                phone: {
                  required: true
                },
                property_address: {
                  required: true
                },
                city: {
                  required: true
                },
                leak_type: {
                  required: true
                },
                consent: {
                  required: true
                }
              }} submitText="Request Leak Inspection" successMessage="Your inspection request has been received. We'll be in touch soon." errorMessage="Failed to submit your request. Please call us directly for immediate help.">
                  <LeakDetectionForm />
                </FormProvider>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}